import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Box } from '@material-ui/core';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { DetailBox } from 'common/components/DetailBox';
import { ICompanyType, useCompanyLazyQuery } from 'graphql/types';
import { Link } from 'common/components/Link';
import { DetailRow } from 'common/components/DetailRow';
import { useSnackbarContext } from 'common/context';

type TFullCompanyType = Pick<
    ICompanyType,
    | 'id'
    | 'name'
    | 'street'
    | 'houseNumber'
    | 'houseNumberAdditional'
    | 'zipCode'
    | 'city'
    | 'btwId'
    | 'fallbackEmail'
    | 'invoiceCompanyName'
    | 'invoiceName'
    | 'invoiceEmail'
>;

interface IProps {
    basic?: boolean;
    className?: string;
    companyData?: {
        company?: Partial<TFullCompanyType>;
        loading: boolean;
        error?: ApolloError;
    };
}

const BaseCompanyDetailBox = ({ basic, className, companyData }: IProps) => {
    const { t } = useTranslation();
    const { setSnackbarState } = useSnackbarContext();
    const [
        fetchCompany,
        { data, loading: loadingCompany, error: errorCompany },
    ] = useCompanyLazyQuery({
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const company = companyData
        ? companyData.company
        : (data?.company as TFullCompanyType);
    const loading = companyData ? companyData.loading : loadingCompany;
    const error = companyData ? companyData.error : errorCompany;

    useEffect(() => {
        if (!companyData) return fetchCompany();
    }, [companyData]);

    return (
        <DetailBox
            className={className}
            description={
                !basic && (
                    <Box component="span">
                        {t('company.pageDescription_1')}{' '}
                        <Box className="underline" component="span">
                            {t('company.pageDescription_2')}
                        </Box>
                    </Box>
                )
            }
            error={!!error}
            id="companyinformation"
            loading={loading}
            showTitleDivider={!basic}
            title={t('company.pageTitle')}
        >
            <Box display="flex" flexWrap="wrap">
                <Box className={`leftContentWrapper ${basic ? 'basic' : ''}`}>
                    <DetailRow
                        bold
                        description={!basic ? t('company.name') : undefined}
                    >
                        {company?.name}
                    </DetailRow>
                    <DetailRow
                        description={!basic ? t('company.address') : undefined}
                    >
                        {`${company?.street} ${company?.houseNumber} ${company?.houseNumberAdditional}`}
                        <br />
                        {`${company?.zipCode} ${company?.city}`}
                    </DetailRow>

                    <Box mt={8}>
                        <DetailRow
                            bold={!!basic}
                            description={t('company.btw')}
                            small={!!basic}
                        >
                            {company?.btwId}
                        </DetailRow>
                    </Box>
                </Box>

                {!basic && (
                    <Box className="rightContentWrapper">
                        <DetailRow bold description={t('company.invoiceName')}>
                            {company?.invoiceCompanyName || company?.name}{' '}
                            <Box component="span" display="block" mt={2}>
                                {company?.invoiceName}
                            </Box>
                        </DetailRow>
                        <Box mt={8}>
                            <DetailRow
                                bold
                                description={t('company.invoiceEmail')}
                            >
                                {company?.invoiceEmail}
                            </DetailRow>
                        </Box>
                    </Box>
                )}
            </Box>
        </DetailBox>
    );
};

export const CompanyDetailBox = styled(BaseCompanyDetailBox)`
    ${Link} {
        font-weight: 700;
    }

    .underline {
        text-decoration: underline;
    }

    .leftContentWrapper {
        width: 100%;

        &.basic {
            margin-right: 0;
            margin-top: ${({ theme }) => `${theme.spacing(6)}px`};
        }
    }

    .rightContentWrapper {
        width: 100%;
        margin-top: ${({ theme }) => `${theme.spacing(8)}px`};
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['lg']}px`}) {
        .leftContentWrapper {
            width: 50%;
            margin-top: 0;
        }

        .rightContentWrapper {
            width: 50%;
            margin-left: auto;
            margin-top: 0;
        }
    }
`;
